"use client";
import { Badge, type BadgeProps } from "../Badge";
import { Skeleton } from "../Skeleton";

export type HealthStatus = "healthy" | "warning" | "error" | "unknown";

export interface DeviceHealthBadgeProps extends Omit<BadgeProps, "variant" | "children"> {
  /** Health status of the device */
  status: HealthStatus;
  /** Number of active alerts */
  alertCount?: number;
  /** Show alert count in the badge */
  showCount?: boolean;
  /** Loading state */
  isLoading?: boolean;
}

/**
 * DeviceHealthBadge
 *
 * Displays device health status with optional alert counts.
 * Maps health status to feedback colors (success/warning/error).
 * Pulsing dot for warning/error states.
 *
 * @example
 * ```tsx
 * <DeviceHealthBadge status="healthy" />
 * <DeviceHealthBadge status="warning" alertCount={2} showCount />
 * <DeviceHealthBadge status="error" alertCount={5} showCount />
 * ```
 */
export function DeviceHealthBadge({
  status,
  alertCount,
  showCount = false,
  isLoading,
  size = "sm",
  shape = "pill",
  ...badgeProps
}: DeviceHealthBadgeProps) {
  if (isLoading) {
    return <Skeleton className="inline-flex h-6 w-20" />;
  }

  const variantMap: Record<HealthStatus, BadgeProps["variant"]> = {
    healthy: "success",
    warning: "warning",
    error: "error",
    unknown: "neutral",
  };

  const labelMap: Record<HealthStatus, string> = {
    healthy: "Healthy",
    warning: "Warning",
    error: "Error",
    unknown: "Unknown",
  };

  const shouldPulse = status === "warning" || status === "error";
  const label = showCount && alertCount ? `${labelMap[status]} (${alertCount})` : labelMap[status];

  return (
    <Badge
      variant={variantMap[status]}
      size={size}
      shape={shape}
      dot={shouldPulse}
      dotPulse={shouldPulse}
      {...badgeProps}
    >
      {label}
    </Badge>
  );
}
